import React, { useEffect } from 'react';

import { useTranslation, FlowStepProps, trackEvent, useComponentTracking } from '@helios/frontend-core';


import { TakeoverProps } from '@palmetto/dsm-components';
import {
  useConnectedAccounts,
  useCreateConnectedAccount,
} from '../../../../api/connected-accounts/useConnectedAccounts';
import { useUpdateConnectedAccountStatus } from '../../../../api/connected-accounts/useUpdateConnectedAccountStatus';
import { useUser } from '../../../../api/user/useUser';
import { getArcadiaConnectUrl } from '../../../../config/arcadiaConfig';

export interface Props extends FlowStepProps {
  propertyId: string;
  providerId: string;
  setHeaderDetails: (details: Partial<TakeoverProps>) => void;
  onDismiss: () => void;
  mfaRefresh?: boolean;
}

const localePrefix = 'utilityProvider.steps.utilityLogin';

const UtilityLogin: React.FC<Props> = ({ setStep, propertyId, providerId, setHeaderDetails, onDismiss }) => {
  const trackingId = 'takeover-utility-login-step';
  const { t } = useTranslation();
  useComponentTracking(trackingId);
  const { updateUserSettings, user } = useUser();

  const {
    mutate: createConnectedAccount,
    isLoading: isCreatingAccount,
    error: createAccountError,
  } = useCreateConnectedAccount();
  const { data: existingAccounts } = useConnectedAccounts({
    params: propertyId ? { propertyId, providerName: 'arcadia' } : undefined,
  });

  const { updateStatus: updateConnectedAccountStatus } = useUpdateConnectedAccountStatus();

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event?.data?.type === 'onEnd') {
        updateUserSettings({
          arcadiaPlugIntervals: providerId ? { providerId } : undefined,
        });

        const connectionStatus = event?.data?.payload?.status;
        setStep('utility-connect-completed', { connectionStatus, propertyId });
      }
      if (event?.data?.type === 'onCredentialsSubmitted') {
        let credentialId = event?.data?.payload?.utilityCredentialId;

        if (credentialId && propertyId) {
          // Arcadia returns credential IDs without the "crd_" prefix from iframe event
          credentialId = `crd_${credentialId}`;
          const existingAccount = existingAccounts?.find(
            (account) => account.propertyId === propertyId && account.providerName === 'arcadia',
          );

          if (!existingAccount) {
            createConnectedAccount({
              propertyId,
              provider: {
                name: 'arcadia',
                id: credentialId,
              },
            });
          } else {
            updateConnectedAccountStatus(existingAccount._id, 'inProgress');
          }
        }

        trackEvent('utility-login-credentials-submitted', {
          credentialId: event?.data?.payload?.utilityCredentialId,
          providerId,
        });

        setHeaderDetails({
          heading: t(`connectUtilityTakeover.takeover-heading.connecting-account`),
          topNavProps: {
            leftButton: {
              onClick: onDismiss,
              text: t('common.cancel'),
            },
          },
        });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [existingAccounts]);

  const originUrl = window.location.origin;
  const src = getArcadiaConnectUrl({
    correlationId: `${propertyId}|${user?._id}`,
    providerId,
    originUrl,
  });

  return (
    <div className="">
      <iframe
        style={{ border: 'none', background: 'white' }}
        height="660px"
        width="100%"
        title={t(`${localePrefix}.heading`)}
        src={src}
      />
    </div>
  );
};

export default UtilityLogin;
